import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  InputAdornment,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Paper
  // useTheme
} from '@mui/material';
import {
  Save,
  Image,
  Add,
  Delete,
  ArrowBack,
  AttachMoney
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import menuService from '../../services/menuService';
import uploadService from '../../services/uploadService';
import SuccessDialog from './dialogs/SuccessDialog';
import AddCategoryDialog from '../category/dialogs/AddCategoryDialog';
import categoryService from '../../services/categoryService';

const AddMenu = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const MotionDiv = motion.div;
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [editingVariation, setEditingVariation] = useState(null);


  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    cost: '',
    categoryId: '',
    image: null,
    isAvailable: true,
    variations: []
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  
  // Image preview
  const [preview, setPreview] = useState(null);
  
  // New variation state
  const [newVariation, setNewVariation] = useState({
    name: '',
    price: ''
  });

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await menuService.getAllCategories();
        setCategories(response);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError(t('menu.fetchCategoriesError'));
      }
    };
    
    fetchCategories();
  }, [t]);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

const handleAddCategory = async (categoryData) => {
  try {
    const newCategory = await categoryService.create(categoryData);
    const updated = await menuService.getAllCategories();
    setCategories(updated);
    // Auto-select the new category if your backend returns its id
    if (newCategory && newCategory.category_id) {
      setFormData(prev => ({
        ...prev,
        categoryId: newCategory.category_id
      }));
    }
  } catch (error) {
    console.error('Error adding category:', error);
    setError(t('menu.addCategoryError'));
  }
};

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) { // 5MB limit
        setErrors(prev => ({
          ...prev,
          image: t('menu.imageSizeError')
        }));
        return;
      }
      
      // Convert to base64 and update form data
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setPreview(base64);
        setFormData(prev => ({
          ...prev,
          image: base64 // Store the base64 string directly
        }));
      };
      reader.readAsDataURL(file);
      
      // Clear error
      setErrors(prev => ({
        ...prev,
        image: null
      }));
    }
  };
  
  // Clear uploaded image
  const handleClearImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null
    }));
    setPreview(null);
  };
  
  // Handle variation field changes
  const handleVariationChange = (e) => {
    const { name, value } = e.target;
    setNewVariation(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // // Add a new variation
  // const handleAddVariation = () => {
  //   if (!newVariation.name || !newVariation.price) return;
    
  //   const priceValue = parseFloat(newVariation.price);
  //   if (isNaN(priceValue) || priceValue < 0) {
  //     setErrors(prev => ({
  //       ...prev,
  //       variation: 'Price must be a valid positive number'
  //     }));
  //     return;
  //   }
    
  //   setFormData(prev => ({
  //     ...prev,
  //     variations: [
  //       ...prev.variations,
  //       {
  //         id: Date.now(), // Temporary ID
  //         name: newVariation.name.trim(),
  //         price: priceValue,
  //         type: 'addon' // Default type for add-on pricing
  //       }
  //     ]
  //   }));
    
  //   // Reset new variation form
  //   setNewVariation({
  //     name: '',
  //     price: ''
  //   });
    
  //   // Clear any variation errors
  //   setErrors(prev => ({
  //     ...prev,
  //     variation: null
  //   }));
  // };
  
  // Remove a variation
  const handleRemoveVariation = (variationId) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.filter(v => v.id !== variationId)
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('menu.nameRequired');
    }
    
    if (!formData.price) {
      newErrors.price = t('menu.priceRequired');
    } else if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) {
      newErrors.price = t('menu.pricePositiveError');
    }
    
    if (formData.cost && (isNaN(parseFloat(formData.cost)) || parseFloat(formData.cost) < 0)) {
      newErrors.cost = t('menu.costPositiveError');
    }
    
    if (!formData.categoryId) {
      newErrors.categoryId = t('menu.categoryRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Process the image through uploadService if provided
      let processedImage = null;
      if (formData.image) {
        try {
          console.log('Processing image using uploadService');
          // uploadService.uploadMenuImage handles both File objects and base64 strings
          // Since formData.image is already base64 from handleImageChange, this validates and returns it
          processedImage = await uploadService.uploadMenuImage(formData.image);
          console.log('Image processing successful');
        } catch (uploadError) {
          console.error('Upload service error:', uploadError);
          setError(uploadError.message || t('menu.imageProcessError'));
          setIsLoading(false);
          return;
        }
      }

      // Prepare menu item data
      const menuItemData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        price: parseFloat(formData.price),
        cost: formData.cost ? parseFloat(formData.cost) : null,
        category_id: parseInt(formData.categoryId),
        image: processedImage, // This will be base64 string or null
        available: formData.isAvailable,
        variations: formData.variations.map(v => ({
          name: v.name.trim(),
          price: parseFloat(v.price)
        }))
      };

      console.log('Submitting menu item data:', {
        ...menuItemData,
        image: menuItemData.image ? 'BASE64_IMAGE_DATA' : null
      });

      // Create menu item via API
      const response = await menuService.create(menuItemData);
      console.log('Menu item created successfully:', response);
      
      // Reset form data
      setFormData({
        name: '',
        description: '',
        price: '',
        cost: '',
        categoryId: '',
        image: null,
        isAvailable: true,
        variations: []
      });
      setPreview(null);
      setErrors({});
      
      // Show success dialog
      setSuccessDialogOpen(true);
      
    } catch (error) {
      console.error('Error adding menu item:', error);
      setError(error.response?.data?.message || error.message || t('menu.createError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditVariation = (variationId) => {
    const variation = formData.variations.find(v => v.id === variationId);
    if (variation) {
      setEditingVariation(variation);
      setNewVariation({
        name: variation.name,
        price: variation.price.toString()
      });
      // Remove the variation temporarily while editing
      setFormData(prev => ({
        ...prev,
        variations: prev.variations.filter(v => v.id !== variationId)
      }));
    }
  };

  // Cancel editing variation
  const handleCancelEditVariation = () => {
    if (editingVariation) {
      // Restore the original variation
      setFormData(prev => ({
        ...prev,
        variations: [...prev.variations, editingVariation]
      }));
    }
    setEditingVariation(null);
    setNewVariation({ name: '', price: '' });
  };

  // Update the existing handleAddVariation function to handle editing:
  const handleAddVariation = () => {
    if (!newVariation.name || !newVariation.price) return;
    
    const priceValue = parseFloat(newVariation.price);
    if (isNaN(priceValue) || priceValue < 0) {
      setErrors(prev => ({
        ...prev,
        variation: 'Price must be a valid positive number'
      }));
      return;
    }
    
    const variationData = {
      id: editingVariation ? editingVariation.id : Date.now(), // Keep original ID if editing
      name: newVariation.name.trim(),
      price: priceValue,
      type: 'addon'
    };
    
    setFormData(prev => ({
      ...prev,
      variations: [...prev.variations, variationData]
    }));
    
    // Reset form and editing state
    setNewVariation({ name: '', price: '' });
    setEditingVariation(null);
    setErrors(prev => ({ ...prev, variation: null }));
  };
  
  // Handle success dialog close
  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    navigate('/dashboard/menu/list');
  };
  
  // Navigate back to menu list
  const handleCancel = () => {
    navigate('/dashboard/menu/list');
  };
  
  return (
    <Box>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {t('menu.addTitle')}
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleCancel}
          >
            {t('menu.backToMenuList')}
          </Button>
        </Box>
      </MotionDiv>
      
      {error && (
        <MotionDiv
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Box sx={{ 
            bgcolor: 'error.light', 
            color: 'error.main', 
            p: 2, 
            borderRadius: 1,
            mb: 3
          }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        </MotionDiv>
      )}
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('menu.basicInformation')}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={3}>
                     {/* Menu Item Name */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('menu.itemName')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={!!errors.name}
                        helperText={errors.name}
                      />
                    </Grid>

                    {/* Category with Create New Category button above */}
                    <Grid item xs={12} sm={6} sx={{ position: 'relative' }}>
                        <Button
                          onClick={() => setAddCategoryOpen(true)}
                          sx={{
                            position: 'absolute',
                            top: -13, 
                            left: 35,
                            p: 0,
                            color: 'primary.main',
                            textDecoration: 'underline',
                            minHeight: 0,
                            minWidth: 0,
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            alignSelf: 'flex-start',
                            zIndex: 1,
                            background: 'transparent',
                            '&:hover': {
                              background: 'none',
                              opacity: 0.8
                            }
                          }}
                        >
                          {t('menu.createNewCategory')}
                        </Button>
                        <AddCategoryDialog
                          open={addCategoryOpen}
                          onClose={() => setAddCategoryOpen(false)}
                          onAdd={handleAddCategory}
                        />

                    <FormControl fullWidth required error={!!errors.categoryId}>
                      <InputLabel>{t('menu.category')}</InputLabel>
                      <Select
                        name="categoryId"
                        value={formData.categoryId}
                        onChange={handleChange}
                        label={t('menu.category')}
                      >
                        {categories.map(category => (
                          <MenuItem key={category.category_id} value={category.category_id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.categoryId && (
                        <FormHelperText>{errors.categoryId}</FormHelperText>
                      )}
                    </FormControl>
                </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('menu.price')}
                        name="price"
                        type="number"
                        value={formData.price}
                        onChange={handleChange}
                        fullWidth
                        required
                        error={!!errors.price}
                        helperText={errors.price}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('menu.costOptional')}
                        name="cost"
                        type="number"
                        value={formData.cost}
                        onChange={handleChange}
                        fullWidth
                        error={!!errors.cost}
                        helperText={errors.cost}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        label={t('menu.descriptionOptional')}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<Image />}
                          sx={{ 
                            mr: 2,
                            background: !preview 
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : 'transparent',
                            color: !preview ? 'white' : 'primary.main',
                            border: preview ? '1px solid' : 'none',
                            borderColor: 'primary.main',
                            '&:hover': {
                              background: !preview 
                                ? 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                                : 'rgba(102, 126, 234, 0.05)'
                            }
                          }}
                        >
                          {preview ? t('menu.changeImage') : t('menu.uploadImage')}
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleImageChange}
                          />
                        </Button>
                        
                        {preview && (
                          <IconButton 
                            color="error" 
                            onClick={handleClearImage}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        )}
                        
                        {errors.image && (
                          <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                            {errors.image}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isAvailable}
                            onChange={handleChange}
                            name="isAvailable"
                            color="primary"
                          />
                        }
                        label={t('menu.itemAvailable')}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </MotionDiv>
            
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card sx={{ mt: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('menu.variationsOptional')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add size variations, add-ons, or different options for this menu item. 
                    Prices will be added to the base price.
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label={editingVariation ? "Edit Variation Name" : "Variation Name"}
                        name="name"
                        value={newVariation.name}
                        onChange={handleVariationChange}
                        fullWidth
                        placeholder="e.g., Medium Size, Extra Shot, Take Away"
                        helperText={editingVariation ? "Editing existing variation" : "Name of the variation or add-on"}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Additional Price"
                        name="price"
                        type="number"
                        value={newVariation.price}
                        onChange={handleVariationChange}
                        fullWidth
                        placeholder="0.50"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="body2" sx={{ mr: 0.5 }}>+</Typography>
                              <AttachMoney />
                            </InputAdornment>
                          ),
                        }}
                        helperText="Additional cost (0 for same price)"
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={2}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          startIcon={<Add />}
                          onClick={handleAddVariation}
                          disabled={!newVariation.name || newVariation.price === ''}
                          sx={{ 
                            height: '40px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                            }
                          }}
                        >
                          {editingVariation ? 'Update' : t('common.add')}
                        </Button>
                        
                        {editingVariation && (
                          <Button
                            variant="outlined"
                            color="inherit"
                            fullWidth
                            onClick={handleCancelEditVariation}
                            sx={{ height: '32px', fontSize: '0.8rem' }}
                          >
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {errors.variation && (
                    <Typography color="error" variant="caption" sx={{ mb: 2, display: 'block' }}>
                      {errors.variation}
                    </Typography>
                  )}
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Current Variations:
                    </Typography>
                    {formData.variations.length === 0 ? (
                      <Typography color="text.secondary" align="center">
                        {t('menu.noVariations')}
                      </Typography>
                    ) : (
                      <Grid container spacing={1}>
                        {formData.variations.map(variation => (
                          <Grid item key={variation.id} xs={12} sm={6}>
                            <Paper 
                              elevation={1} 
                              sx={{ 
                                p: 2, 
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1, mr: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {variation.name}
                                  </Typography>
                                  <Typography variant="body2" color="primary">
                                    +${variation.price.toFixed(2)}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleEditVariation(variation.id)}
                                    color="primary"
                                    sx={{ '&:hover': { bgcolor: 'primary.lighter' } }}
                                  >
                                    <Image fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleRemoveVariation(variation.id)}
                                    color="error"
                                    sx={{ '&:hover': { bgcolor: 'error.lighter' } }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </MotionDiv>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('menu.preview')}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    {preview ? (
                      <Box
                        component="img"
                        src={preview}
                        alt={t('menu.itemPreviewAlt')}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 2,
                          mb: 2
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'action.hover',
                          borderRadius: 2,
                          mb: 2
                        }}
                      >
                        <Typography color="text.secondary">
                          {t('menu.noImageUploaded')}
                        </Typography>
                      </Box>
                    )}
                    
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formData.name || t('menu.itemName')}
                    </Typography>
                    
                    <Typography variant="body1" color="primary.main" sx={{ fontWeight: 500, my: 1 }}>
                      ${formData.price ? parseFloat(formData.price).toFixed(2) : '0.00'}
                    </Typography>
                    
                    <Chip 
                      label={categories.find(c => c.category_id === parseInt(formData.categoryId))?.name || t('menu.category')}
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      {formData.description || t('menu.itemDescriptionPlaceholder')}
                    </Typography>
                    
                    {formData.variations.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {t('menu.variations')}:
                        </Typography>
                        
                        {formData.variations.map(variation => (
                          <Box key={variation.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{variation.name}</Typography>
                            <Typography variant="body2">${variation.price.toFixed(2)}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
              
              <Card sx={{ mt: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<Save />}
                    disabled={isLoading}
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                      }
                    }}
                  >
                    {isLoading ? t('menu.saving') : t('menu.saveMenuItem')}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    onClick={handleCancel}
                    sx={{ mt: 2 }}
                  >
                    {t('common.cancel')}
                  </Button>
                </CardContent>
              </Card>
            </MotionDiv>
          </Grid>
        </Grid>
      </form>
      
      {/* Success Dialog */}
      <SuccessDialog 
        open={successDialogOpen} 
        onClose={handleSuccessDialogClose} 
        message={t('menu.createSuccess')}
      />
    </Box>
  );
};

export default AddMenu;